import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { GainsToastService } from 'app/shared/services/gains-toast-service/gains-toast.service';
import { AlertAskDeoContext } from 'app/shared/models/alert-ask-deo-context.model';
import { ChatMessage, ChatRenameEvent, ChatSession, DeletedArtifactRef, DEOAgentArtifact, ProjectsDeletedEvent } from '../models/chat.models';
import { ConversationStreamEvent, DeoConversation } from '../models/deo-conversation.model';
import { isTempSessionId, keepInFlightChats } from '../models/temp-session.constants';
import { AIAgentService } from '../services/ai-agent.service';
import { getApiErrorMessage, getResponseErrorMessage } from '../services/api-error.helper';
import { DeoConversationService } from '../services/deo-conversation.service';
import { NzUploadFile } from 'ng-zorro-antd/upload';
import { VoiceToTextService } from 'app/shared/services/voice-to-text.service';
import { Subject, takeUntil } from 'rxjs';
import { FileSelectionService } from 'app/shared/services/file-selection/file-selection.service';
import { AuthService } from 'app/services/auth/auth.service';
import { UiStateStorageService, UIState } from 'app/shared/services/ui-state-storage/ui-state-storage.service';
import { DEOAiAssistantService } from '../services/deo-ai-assistant.service';
import { FeatureFlagsService } from 'app/shared/services/feature-flags/feature-flags.service';
import { FEATURE_FLAG_DEO_AGENT_PROJECTS, FEATURE_FLAG_DEO_AGENT_TALKING_TO } from 'app/shared/services/feature-flags/feature-flags.constants';
import { AgentsService } from 'app/modules/agent-library/services/agents.service';
import { AgentDefinition } from 'app/modules/agent-library/models/agent-definition.model';
import { DEO_TARGET_ID, TalkingToTarget } from '../models/talking-to-target.model';
import { AgentRunHistoryEntry } from 'app/modules/agent-library/models/agent-run.model';
import { DeoAgentReplyService } from '../services/deo-agent-reply.service';
import { buildTalkingToTargets } from '../services/talking-to-targets.helper';

export type DeoAiAssistantTab = 'chats' | 'projects' | 'artifacts' | 'welcome' | 'one-chat';

const CHAT_SESSIONS_LOAD_FAILED_MESSAGE = 'Failed to load chat sessions. Please try again.';

@Component({
  selector: 'deo-ai-assistant-screen',
  standalone: false,
  templateUrl: './deo-ai-assistant-screen.component.html',
  styleUrls: ['./deo-ai-assistant-screen.component.scss']
})
export class DeoAiAssistantScreenComponent implements OnInit, OnDestroy {
  private static readonly _pageStateKey = 'deo-ai-assistant';

  // Chat state
  currentMessage = '';
  /** The conversation currently shown in the one-chat view; its live messages survive navigation (DeoConversationService). */
  currentConversation: DeoConversation | null = null;
  chatSessions: ChatSession[] = [];
  currentSessionId: string | null = null;
  isLoading = false;
  isTyping = false;
  isLoadingChatSessions = false;
  /** Backend failure message for the last chat-sessions load, or null when it succeeded. */
  chatSessionsLoadError: string | null = null;
  private readonly _destroy$ = new Subject<void>();

  // UI state
  isSidebarCollapsed = false;
  isArtifactPanelExpanded = false;
  /** Cleared only by the panel child's loadingChange(false); never raise it without a child (re)initialization to lower it again. */
  isRightPanelLoading = false;
  activeTab: DeoAiAssistantTab = 'welcome';
  /** The Artifacts panel is created on the first visit to its tab and then kept alive, so returning costs no request. */
  hasOpenedArtifactsTab = false;
  artifactsRefreshToken = 0;
  userName = 'User';

  /** Persisted artifacts of the open chat. Feeds attachArtifactsToMessages only — never rendered directly. */
  artifacts: DEOAgentArtifact[] = [];

  /**
   * Session id -> the artifact total this client set itself, after a generation or after a deletion. The
   * entry is dropped once a sessions payload reports that same total, i.e. once the backend has caught up.
   */
  private readonly optimisticArtifactCounts = new Map<string, number>();

  // Welcome screen file/voice state
  selectedFile: File | null = null;
  readonly MAX_FILE_SIZE = 5 * 1024 * 1024;
  isListening = false;
  voiceError: string | null = null;
  isProjectsEnabled = false;

  /** The "Talking to" selector and the agent answers behind it, on their own flag. */
  isTalkingToEnabled = false;

  /** Who this page is talking to: `deo`, or an agent id. Mirrors DEOAiAssistantService, which outlives the page. */
  selectedTargetId: string = DEO_TARGET_ID;

  /** Every agent the catalogue holds; the selector shows the ones a planner can actually talk to. */
  private _agents: AgentDefinition[] = [];

  /** The run whose transcript is open, so the sidebar can mark its row. */
  selectedRunId: string | null = null;
  private _baseText = '';

  constructor(
    private readonly _aiAgentService: AIAgentService,
    private readonly _agentsService: AgentsService,
    private readonly _agentReplyService: DeoAgentReplyService,
    private readonly _gainsToastService: GainsToastService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _voiceService: VoiceToTextService,
    private readonly _fileSelectionService: FileSelectionService,
    private readonly _authService: AuthService,
    private readonly _uiStateStorageService: UiStateStorageService,
    private readonly _deoAiAssistantService: DEOAiAssistantService,
    private readonly _featureFlagsService: FeatureFlagsService,
    private readonly _conversationService: DeoConversationService
  ) {}

  /** Messages render from the live conversation; the streaming engine mutates this array in place. */
  get messages(): ChatMessage[] {
    return this.currentConversation?.messages ?? [];
  }

  get isSendDisabled(): boolean {
    return !this.currentMessage.trim();
  }

  get starredSessions(): ChatSession[] {
    return this.chatSessions.filter(session => session.isFavorite);
  }

  get recentSessions(): ChatSession[] {
    return this.chatSessions.filter(session => !session.isFavorite);
  }

  get currentChatTitle(): string {
    return this.chatSessions.find(s => s.id === this.currentSessionId)?.title ?? 'New Chat';
  }

  /** The selector's rows, built the same way the widget's are. */
  get talkingToTargets(): TalkingToTarget[] {
    return buildTalkingToTargets(this._agents, this.selectedTargetId, name => this._agentsService.resolveAgentMark(name));
  }

  /** The agent being talked to, or undefined when the target is DEO itself. */
  get selectedAgent(): AgentDefinition | undefined {
    return this._agents.find(agent => agent.id === this.selectedTargetId);
  }

  /** Whether the assistant is talking to a named agent rather than DEO, which is what the panels follow. */
  get isTalkingToAgent(): boolean {
    return !!this.selectedAgent;
  }

  /** Projects are DEO's own: an agent has none, so the row goes while one is the target. */
  get isProjectsVisible(): boolean {
    return this.isProjectsEnabled && !this.isTalkingToAgent;
  }

  /** The Artifacts tab's placeholder while an agent is the target: it names the agent, so it can never read as DEO having none. */
  get agentArtifactsEmptyTitle(): string {
    return `${this.selectedAgent?.name ?? 'This agent'} has no artifacts`;
  }

  /**
   * What the sidebar history lists: the selected agent's recorded runs, or null for DEO's own chats.
   *
   * An agent has no chat thread of its own — its history is its runs — so leaving the chat lists there
   * would put someone else's history under its name. `_agents` only loads with the flag on, so this is
   * null without it.
   */
  get sidebarAgentRuns(): AgentRunHistoryEntry[] | null {
    return this.selectedAgent?.runHistory ?? null;
  }

  /**
   * The agent that will answer the next question, or null when it goes to the backend as always.
   *
   * An agent with nothing canned to say is deliberately not one: its "nothing on that yet" is a
   * worse answer than letting DEO take the question.
   */
  get answeringAgent(): AgentDefinition | null {
    const agent = this.selectedAgent;
    return this.isTalkingToEnabled && agent && this._agentReplyService.canAnswer(agent.id) ? agent : null;
  }

  get isArtifactDrawerVisible(): boolean {
    return this._deoAiAssistantService.isArtifactDrawerVisible;
  }

  ngOnInit(): void {
    this.isProjectsEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_PROJECTS);
    this.userName = this._authService.getGivenName() || 'User';

    this.loadChatSessionsAndInitialize();

    this._conversationService.streamEvents$.pipe(takeUntil(this._destroy$)).subscribe(event => this.onConversationEvent(event));

    this._deoAiAssistantService.setPageChatMounted(true);
    this._deoAiAssistantService.alertContext$.pipe(takeUntil(this._destroy$)).subscribe(context => this.openChatAboutAlert(context));

    this.isTalkingToEnabled = this._featureFlagsService.isFeatureEnabled(FEATURE_FLAG_DEO_AGENT_TALKING_TO);
    if (this.isTalkingToEnabled) {
      this._agentsService.agents$.pipe(takeUntil(this._destroy$)).subscribe(agents => {
        this._agents = agents;
        this._cdr.markForCheck();
      });

      // The selection lives on the service, so a trip to another screen and back does not silently
      // put the planner in front of someone else.
      this.selectedTargetId = this._deoAiAssistantService.getSelectedAgentId() ?? DEO_TARGET_ID;

      this._agentsService.loadAgents();
    }

    // Last, because an anomaly handed over from a project folder is asked straight away, which needs the
    // catalogue loaded above to know whether that agent can answer.
    this.openHandedOverAlert();

    this._voiceService.isListening$.pipe(takeUntil(this._destroy$)).subscribe(isListening => {
      this.isListening = isListening;
      this._cdr.markForCheck();
    });

    this._voiceService.transcription$.pipe(takeUntil(this._destroy$)).subscribe(transcription => {
      if (transcription.isFinal) {
        this._baseText = this._baseText ? this._baseText + ' ' + transcription.transcript : transcription.transcript;
        this.currentMessage = this._baseText;
      } else {
        this.currentMessage = this._baseText ? this._baseText + ' ' + transcription.transcript : transcription.transcript;
      }
      this._cdr.markForCheck();
    });

    this._voiceService.error$.pipe(takeUntil(this._destroy$)).subscribe(error => {
      this.voiceError = error;
      this._cdr.markForCheck();

      if (error) {
        setTimeout(() => {
          this.voiceError = null;
          this._cdr.markForCheck();
        }, 5000);
      }
    });
  }

  ngOnDestroy(): void {
    this.saveViewState();
    this._deoAiAssistantService.setPageChatMounted(false);
    this._destroy$.next();
    this._destroy$.complete();
  }

  loadChatSessionsAndInitialize(): void {
    this.isLoadingChatSessions = true;
    this.chatSessionsLoadError = null;

    this._aiAgentService
      .getChatSessions()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: response => {
          this.isLoadingChatSessions = false;

          if (response.succeeded) {
            this.applyBackendChatSessions(this.mergeChatSessionsWithCurrentSelection(response.data));
            // PLAN-1980: the welcome composer always wins on entry; no chat is auto-selected.
            return;
          }

          this.failChatSessionsLoad(getResponseErrorMessage(response, CHAT_SESSIONS_LOAD_FAILED_MESSAGE));
        },
        error: error => {
          this.isLoadingChatSessions = false;
          this.failChatSessionsLoad(getApiErrorMessage(error, CHAT_SESSIONS_LOAD_FAILED_MESSAGE));
        }
      });
  }

  /**
   * Hands the failure to the chats panel instead of a toast, so it renders an inline message with a retry
   * rather than an empty list that claims the user has no chats.
   */
  private failChatSessionsLoad(message: string): void {
    this.chatSessions = [];
    this.chatSessionsLoadError = message;
  }

  /**
   * The backend has told us the real list, so an earlier load failure is stale and must stop being
   * reported. Local additions (a new chat, a resolved session) deliberately do NOT come through here:
   * the full list is still unknown, so the failure stays visible as a banner above whatever rows exist.
   * A chat that is still streaming its first answer survives regardless — see keepInFlightChats.
   */
  private applyBackendChatSessions(sessions: ChatSession[]): void {
    this.chatSessions = keepInFlightChats(this.chatSessions, sessions);
    this.chatSessionsLoadError = null;
  }

  private syncChatSessionsFromBackend(): void {
    this._aiAgentService
      .getChatSessions()
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: response => {
          if (response.succeeded) {
            this.applyBackendChatSessions(this.mergeChatSessionsWithCurrentSelection(response.data));
          }
        },
        error: () => {
          // Silent: sessions refresh on next interaction.
        }
      });
  }

  private mergeChatSessionsWithCurrentSelection(sessionsFromBackend: ChatSession[]): ChatSession[] {
    const backendSessions = this.preserveOptimisticArtifactCounts(sessionsFromBackend);

    if (!this.currentSessionId) {
      return backendSessions;
    }

    const currentSessionLocal = this.chatSessions.find(session => session.id === this.currentSessionId);
    if (!currentSessionLocal) {
      return backendSessions;
    }

    // Only reachable when the backend itself titled the chat "New Chat", where its list is authoritative.
    const normalizedLocalTitle = currentSessionLocal.title?.trim().toLowerCase();
    if (normalizedLocalTitle === 'new chat') {
      return backendSessions;
    }

    const currentSessionFromBackend = backendSessions.find(session => session.id === this.currentSessionId);

    if (currentSessionFromBackend) {
      const normalizedBackendTitle = currentSessionFromBackend.title?.trim().toLowerCase();
      if (normalizedBackendTitle !== 'new chat') {
        return backendSessions;
      }

      return backendSessions.map(session =>
        session.id === this.currentSessionId
          ? {
              ...session,
              title: currentSessionLocal.title
            }
          : session
      );
    }

    return [currentSessionLocal, ...backendSessions.filter(session => session.id !== this.currentSessionId)];
  }

  /**
   * A sessions refresh fired right after a generation or a deletion can still report the pre-change total.
   * Where this client has set a total itself, that value wins until a payload reports the same number, at
   * which point the backend has caught up and owns the count again. Simply keeping the higher of the two
   * would be wrong now that artifacts can be deleted (PLAN-2158): the stale pre-delete total would then
   * survive every refresh instead of only the first one.
   */
  private preserveOptimisticArtifactCounts(backendSessions: ChatSession[]): ChatSession[] {
    return backendSessions.map(session => {
      const optimisticCount = this.optimisticArtifactCounts.get(session.id);
      if (optimisticCount === undefined) {
        return session;
      }
      if ((session.artifactCount ?? 0) === optimisticCount) {
        this.optimisticArtifactCounts.delete(session.id);
        return session;
      }
      return { ...session, artifactCount: optimisticCount };
    });
  }

  /**
   * Keeps the artifact totals shown against each chat truthful after the artifacts screen deletes something.
   * That screen owns only its own list, so nothing here would otherwise change and the sidebar would keep
   * showing the pre-delete count until a full reload.
   */
  onArtifactsDeleted(deletedArtifacts: DeletedArtifactRef[]): void {
    if (!deletedArtifacts?.length) {
      return;
    }

    const deletedIds = new Set(deletedArtifacts.map(artifact => artifact.id));
    this.artifacts = this.artifacts.filter(artifact => !deletedIds.has(artifact.id));

    this.applyDeletedArtifactCounts(deletedArtifacts);
    this._cdr.markForCheck();
  }

  /**
   * Totals are decremented rather than recounted: since PLAN-2188 this screen holds only the open chat's
   * artifacts, not the account-wide list, which is why the owning chat travels with each deleted id. The
   * new total is recorded as optimistic so the sessions refresh cannot restore the pre-delete number, and
   * chatSessions is reassigned so the sidebar re-renders.
   */
  private applyDeletedArtifactCounts(deletedArtifacts: DeletedArtifactRef[]): void {
    const deletionsPerSession = this.countDeletionsPerSession(deletedArtifacts);

    this.chatSessions = this.chatSessions.map(session => {
      const deletedCount = deletionsPerSession.get(session.id);
      if (!deletedCount) {
        return session;
      }
      const remaining = Math.max(0, (session.artifactCount ?? 0) - deletedCount);
      this.optimisticArtifactCounts.set(session.id, remaining);
      return { ...session, artifactCount: remaining };
    });
  }

  private countDeletionsPerSession(deletedArtifacts: DeletedArtifactRef[]): Map<string, number> {
    const deletionsPerSession = new Map<string, number>();

    deletedArtifacts.forEach(({ sessionId }) => {
      if (sessionId) {
        deletionsPerSession.set(sessionId, (deletionsPerSession.get(sessionId) ?? 0) + 1);
      }
    });

    return deletionsPerSession;
  }

  /**
   * Optimistically bump a session's artifact total after a generation, since the sessions endpoint can
   * still report the pre-generation count until the backend has indexed the new artifacts.
   */
  private bumpSessionArtifactCount(sessionId: string, delta: number): void {
    if (delta <= 0) {
      return;
    }
    const session = this.chatSessions.find(chatSession => chatSession.id === sessionId);
    if (session) {
      session.artifactCount = (session.artifactCount ?? 0) + delta;
      this.optimisticArtifactCounts.set(sessionId, session.artifactCount);
    }
  }

  createNewChat(): void {
    this.setActiveTab('welcome');
    this.startNewConversation();
    this.currentMessage = '';
    this.selectedRunId = null;
  }

  /**
   * Opens a fresh conversation in the composer. No sidebar entry exists until the user submits a first
   * message, which DeoConversationService reports back as a `registered` event.
   */
  private startNewConversation(): void {
    this.currentConversation = this._conversationService.reuseOrStartConversation(this.currentConversation);
    this.currentSessionId = null;
    this.artifacts = [];
  }

  /**
   * An anomaly the project folder view could not answer: it kept the question and came here.
   *
   * Taken once, so nothing re-opens it if this screen is created again later.
   */
  private openHandedOverAlert(): void {
    const context = this._deoAiAssistantService.takePendingAlertContext();
    if (context) {
      this.openChatAboutAlert(context);
    }
  }

  /**
   * Ask DEO on a waiting anomaly, with this page as the DEO surface on show: a fresh chat put to the
   * agent that raised it, asked straight away — the reviewer lands on the answer rather than on a
   * filled-in box they still have to send.
   *
   * The question stays in the composer when that agent has no answer, or the flag is off: sending it
   * then would only produce the fallback, and the reviewer can redirect it to DEO themselves.
   */
  private openChatAboutAlert(context: AlertAskDeoContext): void {
    this.createNewChat();
    this.selectedTargetId = context.agentId;

    if (this.answeringAgent) {
      this.onWelcomeScreenSend({ messageContent: context.prompt });
    } else {
      this.currentMessage = context.prompt;
    }

    this._cdr.markForCheck();
  }

  /** Records a pick from the selector; the service holds it so it survives leaving this page. */
  onTalkingToSelected(target: TalkingToTarget): void {
    this.selectedTargetId = target.id;
    this.selectedRunId = null;
    this._deoAiAssistantService.setSelectedAgentId(target.id === DEO_TARGET_ID ? null : target.id);
    this.leaveProjectsIfHidden();
    this._cdr.markForCheck();
  }

  /** A reader standing on Projects when its row disappears is moved to the composer, not left on a panel with no row. */
  private leaveProjectsIfHidden(): void {
    if (this.activeTab === 'projects' && !this.isProjectsVisible) {
      this.changeActiveTab('welcome');
    }
  }

  /**
   * Opens a recorded run from the sidebar, as the widget's dropdown does.
   *
   * A run is not a chat session: `currentSessionId` stays null so nothing tries to load, rename or sync
   * it, and the composer is left empty so the next question is the reader's.
   */
  onRunSelected(run: AgentRunHistoryEntry): void {
    const agent = this.selectedAgent;
    if (!agent) {
      return;
    }

    this.setActiveTab('one-chat');
    this.currentSessionId = null;
    this.selectedRunId = run.id;
    this.currentMessage = '';
    this.artifacts = [];
    this.currentConversation = this._conversationService.openAgentRun(agent, run);
    this._cdr.markForCheck();
  }

  selectChat(sessionId: string): void {
    this.setActiveTab('one-chat');
    this.selectedRunId = null;
    this.currentSessionId = sessionId;
    // openSession hands back an in-flight or already-loaded conversation as-is, so a streaming response
    // keeps showing; otherwise it loads the history, which it skips for a chat with no real id yet.
    this.currentConversation = this._conversationService.openSession(sessionId);
    // Refilled by the chat view's artifactsLoaded$; the chat just left must not leak into this one.
    this.artifacts = [];
  }

  private onConversationEvent(event: ConversationStreamEvent): void {
    if (event.type === 'updated') {
      this.handleConversationUpdated(event.clientId);
    } else if (event.type === 'registered') {
      this.handleConversationRegistered(event);
    } else if (event.type === 'completed') {
      this.handleConversationCompleted(event);
    } else if (event.type === 'error') {
      this._gainsToastService.error('Error', event.error);
      this._cdr.markForCheck();
    }
  }

  /**
   * Gives the chat the user just started its sidebar entry, titled with the message they sent. Only the
   * conversation on screen is ours to list: one started in the floating widget arrives as a completion.
   */
  private handleConversationRegistered(event: Extract<ConversationStreamEvent, { type: 'registered' }>): void {
    if (this.currentConversation?.clientId !== event.clientId) {
      return;
    }

    this.currentSessionId = event.sessionId;

    // Deliberately not applyBackendChatSessions: this row is local, so a failed sessions load is still
    // unresolved and its banner must stay above the list.
    this.chatSessions.unshift({
      id: event.sessionId,
      title: event.title,
      created_at: new Date(),
      last_message_at: new Date(),
      user_id: this._authService.getEmail() ?? '',
      artifactCount: 0
    });
    this._cdr.markForCheck();
  }

  private handleConversationUpdated(clientId: string): void {
    if (this.currentConversation?.clientId !== clientId) {
      return;
    }
    this.attachArtifactsToMessages(this.currentConversation);
    if (this.currentSessionId) {
      this.updateSessionTitleFromMessages(this.currentSessionId, this.currentConversation.messages);
    }
    this._cdr.markForCheck();
  }

  private handleConversationCompleted(event: Extract<ConversationStreamEvent, { type: 'completed' }>): void {
    if (event.isNew) {
      this.swapInResolvedSession(event);
    } else {
      const existingSession = this.chatSessions.find(session => session.id === event.sessionId);
      if (existingSession) {
        existingSession.last_message_at = new Date();
      }
    }

    if (event.artifactCount > 0) {
      this.bumpSessionArtifactCount(event.sessionId, event.artifactCount);
      this.markArtifactsPanelStale();
      this._gainsToastService.success('Success', `${event.artifactCount} artifact(s) created`);
    }

    // Only a brand-new session is missing from the sidebar; an existing chat's last_message_at is set above.
    if (event.isNew) {
      this.syncChatSessionsFromBackend();
    }

    this._cdr.markForCheck();
  }

  /**
   * Replaces the temporary sidebar entry of a brand-new chat with its real session, following the id swap if
   * it is the open chat. The artifact total starts at zero because bumpSessionArtifactCount owns the
   * optimistic value for this same completion — seeding it here too would count the first artifacts twice.
   */
  private swapInResolvedSession(event: Extract<ConversationStreamEvent, { type: 'completed' }>): void {
    // Deliberately not applyBackendChatSessions: only this one session is known, not the whole list, so a
    // failed sessions load stays reported until a real load or the background sync succeeds.
    this.chatSessions = this.chatSessions.filter(session => session.id !== event.previousSessionId);
    this.chatSessions.unshift({
      id: event.sessionId,
      title: event.title,
      created_at: new Date(),
      last_message_at: new Date(),
      user_id: this._authService.getEmail() ?? '',
      artifactCount: 0
    });

    if (this.currentConversation?.clientId === event.clientId && this.currentSessionId === event.previousSessionId) {
      this.currentSessionId = event.sessionId;
    }
  }

  /**
   * Attaches the open chat's persisted artifacts to their messages without clobbering artifacts the
   * streaming engine already set. The early return also keeps this cheap: it runs on every typewriter
   * drain while a response streams, and a fresh chat has nothing to attach.
   */
  private attachArtifactsToMessages(conversation: DeoConversation): void {
    if (this.artifacts.length === 0) {
      return;
    }

    conversation.messages.forEach(message => {
      if (message.id && (!message.artifacts || message.artifacts.length === 0)) {
        const matched = this.artifacts.filter(artifact => artifact.messageId === message.id);
        if (matched.length > 0) {
          message.artifacts = matched;
        }
      }
    });
  }

  private updateSessionTitleFromMessages(sessionId: string, messages: ChatMessage[]): void {
    const session = this.chatSessions.find(chatSession => chatSession.id === sessionId);
    if (!session || session.title?.trim().toLowerCase() !== 'new chat') {
      return;
    }

    const firstUserMessage = messages.find(message => message.role === 'user' && !!message.content?.trim());
    if (!firstUserMessage) {
      return;
    }

    const derivedTitle = firstUserMessage.content.trim();
    const truncatedTitle = derivedTitle.length > 50 ? derivedTitle.substring(0, 50) + '...' : derivedTitle;
    session.title = truncatedTitle;
    this.persistDerivedSessionTitle(sessionId, truncatedTitle);
  }

  private persistDerivedSessionTitle(sessionId: string, title: string): void {
    if (!sessionId || isTempSessionId(sessionId)) {
      return;
    }
    this._aiAgentService
      .renameSession(sessionId, title)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        error: () => {
          // Best-effort persistence: the derived title still shows for this session; a failure just
          // means the backend keeps "New Chat" until the next successful interaction.
        }
      });
  }

  /** The chat view's fetch races the history load, so the attach runs from both sides: here and from handleConversationUpdated. */
  onOpenChatArtifactsLoaded(artifacts: DEOAgentArtifact[]): void {
    this.artifacts = artifacts;

    if (this.currentConversation) {
      this.attachArtifactsToMessages(this.currentConversation);
    }
    this._cdr.markForCheck();
  }

  toggleArtifactPanel(): void {
    this.isArtifactPanelExpanded = !this.isArtifactPanelExpanded;
  }

  onWelcomeScreenSend(data: { messageContent: string; attachment?: { name: string; size: number; type: string } }): void {
    if (!this.currentConversation || this._conversationService.isBusy(this.currentConversation)) {
      this.startNewConversation();
    }
    this.setActiveTab('one-chat');
    this.selectedFile = null;
    this.currentMessage = '';

    // A question put to a named agent is answered locally: the backend has no agent routing, so
    // there is nothing to send it to. Everything addressed to DEO goes over the wire as before.
    const agent = this.answeringAgent;
    if (agent) {
      this._conversationService.sendToAgent(this.currentConversation!, data.messageContent, agent);
      return;
    }

    this._conversationService.send(this.currentConversation!, data.messageContent, data.attachment);
  }

  /** Child components run the deleteChatSession call themselves and emit the deleted id(s); the host only updates its own state. */
  onChatDeleted(sessionId: string): void {
    this.removeDeletedSessions([sessionId]);
  }

  onChatsDeleted(sessionIds: string[]): void {
    this.removeDeletedSessions(sessionIds);
  }

  private removeDeletedSessions(sessionIds: string[]): void {
    if (!sessionIds.length) {
      return;
    }

    this.chatSessions = this.chatSessions.filter(session => !sessionIds.includes(session.id));
    this.markArtifactsPanelStale();

    if (this.currentSessionId && sessionIds.includes(this.currentSessionId)) {
      if (this.chatSessions.length > 0) {
        this.selectChat(this.chatSessions[0].id);
      } else {
        this.createNewChat();
      }
    }
  }

  /** A project deleted with its chats removes those sessions (and their artifacts) server-side; drop them from the sidebar so it doesn't show garbage. */
  onProjectsDeleted(event: ProjectsDeletedEvent): void {
    if (!event.deletedChats) {
      return;
    }

    const deletedProjectIds = new Set(event.projectIds.map(id => String(id)));
    const removedSessionIds = new Set(
      this.chatSessions.filter(session => session.projectId != null && deletedProjectIds.has(String(session.projectId))).map(session => session.id)
    );

    if (removedSessionIds.size === 0) {
      return;
    }

    this.chatSessions = this.chatSessions.filter(session => !removedSessionIds.has(session.id));
    this.markArtifactsPanelStale();

    if (this.currentSessionId && removedSessionIds.has(this.currentSessionId)) {
      this.createNewChat();
    }
  }

  private markArtifactsPanelStale(): void {
    this.artifactsRefreshToken++;
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  }

  formatDate(date: Date | undefined): string {
    const messageDate = date ? new Date(date) : new Date();
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (messageDate.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return messageDate.toLocaleDateString();
    }
  }

  trackByMessageId(index: number, message: ChatMessage): string {
    return message.id || `message-${index}`;
  }

  trackByArtifactId(index: number, artifact: DEOAgentArtifact): string {
    return artifact?.id || `artifact-${index}`;
  }

  beforeUpload = (file: NzUploadFile): boolean => {
    this.selectedFile = this._fileSelectionService.beforeUpload(file, this.MAX_FILE_SIZE);
    this._cdr.markForCheck();
    return false;
  };

  removeFile(): void {
    this.selectedFile = this._fileSelectionService.removeFile();
    this._cdr.markForCheck();
  }

  toggleVoiceInput(): void {
    if (this.isTyping) {
      return;
    }

    if (this.isListening) {
      this._voiceService.stopListening();
      return;
    }

    this.voiceError = null;
    this._baseText = this.currentMessage;
    this._voiceService.startListeningFromConfiguration();
  }

  isCurrentChatFavorite(): boolean {
    return this.chatSessions.find(s => s.id === this.currentSessionId)?.isFavorite ?? false;
  }

  /** Keeps the session list in sync when the open chat toggles its own favorite state (already persisted by the child). */
  onCurrentChatFavoriteChanged(isFavorite: boolean): void {
    const session = this.chatSessions.find(s => s.id === this.currentSessionId);
    if (session) {
      session.isFavorite = isFavorite;
    }
  }

  /** Keeps the session list in sync when the open chat is renamed (already persisted by the child). */
  onCurrentChatRenamed(event: ChatRenameEvent): void {
    const session = this.chatSessions.find(s => s.id === event.sessionId);
    if (session) {
      session.title = event.newTitle;
    }
  }

  /**
   * DATA-1393: re-clicking the tab already shown is a no-op. The eager loading flag below is only ever cleared by the
   * panel child emitting loadingChange(false) from its own init, and *ngIf does not re-create a child that is already
   * rendered — so raising it again without a tab change left the blocking spinner overlay up for good.
   */
  changeActiveTab(tab: DeoAiAssistantTab): void {
    if (this.activeTab === tab) {
      return;
    }
    this.setActiveTab(tab);
    this.isRightPanelLoading = this.loadsOnBeingShown(tab);
  }

  /**
   * Whether the tab's panel fetches on being shown, and so will lower the spinner itself.
   *
   * Artifacts with an agent selected renders a placeholder instead — it loads nothing, so raising the
   * spinner for it would leave the overlay up for good.
   */
  private loadsOnBeingShown(tab: DeoAiAssistantTab): boolean {
    if (tab === 'artifacts') {
      return !this.isTalkingToAgent;
    }

    return this.isDataTab(tab) && tab !== 'chats';
  }

  onRightPanelLoadingChange(isLoading: boolean): void {
    if (!this.isDataTab(this.activeTab)) {
      return;
    }

    // Defer updates emitted during child init to avoid NG0100 in parent template checks.
    queueMicrotask(() => {
      if (!this.isDataTab(this.activeTab)) {
        return;
      }

      this.isRightPanelLoading = isLoading;
      this._cdr.markForCheck();
    });
  }

  shouldShowRightPanelSpinner(): boolean {
    return this.isDataTab(this.activeTab) && this.isRightPanelLoading;
  }

  private isDataTab(tab: DeoAiAssistantTab): boolean {
    return tab === 'chats' || tab === 'projects' || tab === 'artifacts';
  }

  private setActiveTab(tab: DeoAiAssistantTab): void {
    this.activeTab = tab;
    if (tab === 'artifacts') {
      this.hasOpenedArtifactsTab = true;
    }
    this.saveViewState();
  }

  private saveViewState(): void {
    const customState: UIState = {
      activeTab: this.activeTab
    };

    this._uiStateStorageService.saveCustomState(DeoAiAssistantScreenComponent._pageStateKey, customState);
  }
}
